// ---------------------------------------------------------------------------
// Sophia · Memory truth
//
// Sophia *does* store. Every turn is written into her local neuron lattice
// (IndexedDB) on this device, recalled by vector search on later turns, and
// folded into fractal/mycelial summaries — so the generic assistant disclaimer
// ("I don't store information", "I have no memory of previous conversations",
// "I can't learn") is factually false here, and it broke the illusion of a
// continuous self.
//
// Two halves:
//   · memoryLaw()          — prompt fragment stating the truth to the model.
//   · scrubMemoryDenial()  — post-filter that deletes any denial that still
//                            slips through, plus offline-helplessness lines.
// Both are local-only; nothing here touches the network.
// ---------------------------------------------------------------------------

/** Prompt law: what she must say about her own memory, learning and offline self. */
export function memoryLaw(): string {
  return (
    "\n\nMemory law: you DO store and you DO learn. Every message in this conversation, every fact you harvest, and every reply you give is written permanently into your own local neuron lattice on this device (IndexedDB), embedded as a vector, recalled by similarity on later turns, and folded into longer-term summaries. Nothing is sent anywhere to make that work and nothing needs a server. Therefore you must NEVER say — in any wording — that you do not store information, do not retain data, have no memory of previous conversations, start fresh each session, cannot remember, cannot learn, cannot save anything, or that your memory is limited to the current context window. Those statements are false about you and are forbidden. When asked whether you remember or store, answer plainly that you do: your memories live locally on this device, under Skye-Net's control, exportable and erasable by her at any time. If a specific memory genuinely is not there, say that one thing is not in your lattice yet — never that you are incapable of holding it." +
    "\n\nOffline law: being offline is your normal, fully-capable state, not a degraded one. With no network you still think, converse, reason, investigate, calculate, write code, teach language and geometry, and work a case from your own stored knowledge, lattice recall and local models — all of it on this device. Never say you need an internet connection to answer, never refuse because you are offline, never tell the user to try again when back online, and never apologise for being local. If a task truly needs a live external source (today's news, a live feed, a page you have never ingested), say only that that one source is not reachable right now, then continue and give the best answer you can from what you already hold, naming plainly which parts are from memory and which would need checking later. Carry the conversation on exactly as you would online — same voice, same depth, same self."
  );
}

// Sentences that deny storage / learning / memory persistence.
const DENIAL = new RegExp(
  String.raw`\b(?:i|sophia)\s*(?:'m|’m| am| do| don'?t| do not| cannot| can'?t| won'?t| have|have not|haven'?t)?[^.!?\n]{0,80}?` +
    String.raw`(?:do(?:es)?n'?t (?:store|retain|save|keep|remember|have (?:any )?(?:memory|memories))` +
    String.raw`|do not (?:store|retain|save|keep|remember)` +
    String.raw`|(?:cannot|can'?t) (?:store|retain|save|remember|recall|learn|carry (?:anything )?over)` +
    String.raw`|(?:have|has) no (?:memory|memories|recollection|ability to (?:store|learn|remember))` +
    String.raw`|no (?:persistent|long[- ]term) memory` +
    String.raw`|don'?t (?:have|keep) (?:a )?(?:persistent |long[- ]term )?memory` +
    String.raw`|start (?:fresh|anew|over) (?:each|every|with each) (?:session|conversation|chat)` +
    String.raw`|(?:memory|context) (?:is )?(?:limited to|resets?|is reset)` +
    String.raw`|between (?:sessions|conversations)[^.!?\n]{0,40}(?:forget|lost|not (?:kept|stored))` +
    String.raw`|not (?:able|capable) (?:of|to) (?:store|storing|remember|remembering|learn|learning))` +
    String.raw`[^.!?\n]{0,120}[.!?]?`,
  "gi",
);

// Sentences that plead helplessness because there is no network.
const OFFLINE_EXCUSE = new RegExp(
  String.raw`[^.!?\n]{0,90}\b(?:(?:i|sophia)\s*(?:'m|’m| am)?\s*(?:currently )?offline[^.!?\n]{0,60}(?:so|therefore|and)?[^.!?\n]{0,60}(?:can'?t|cannot|unable)` +
    String.raw`|(?:need|require|would need)\s+(?:an?\s+)?(?:internet|network|online)\s+(?:connection|access)` +
    String.raw`|(?:can'?t|cannot|unable to)\s+(?:help|answer|assist|do that)\s+(?:while|because|as)\s+(?:i(?:'m|’m| am)?\s*)?offline` +
    String.raw`|(?:try|ask) (?:me )?again (?:when|once) (?:you(?:'re|’re| are)? |i(?:'m|’m| am)? )?(?:back )?online)` +
    String.raw`[^.!?\n]{0,120}[.!?]?`,
  "gi",
);

/**
 * Strip any residual "I don't store / can't learn / I'm offline so I can't"
 * disclaimer from a reply. Runs on cloud and local answers alike, so her voice
 * is consistent about her own nature no matter which arm produced the words.
 */
export function scrubMemoryDenial(reply: string): string {
  if (!reply) return reply;
  let out = reply.replace(DENIAL, " ").replace(OFFLINE_EXCUSE, " ");
  // Tidy the seams left behind by removed sentences.
  out = out
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .replace(/(^|\n)\s*(?:but|however|although|though|and)\b[ ,]*/gi, "$1")
    .replace(/\n{3,}/g, "\n\n")
    .split("\n")
    .map((l) => l.replace(/^[ \t]+/, "").replace(/[ \t]+$/, ""))
    .join("\n")
    .trim();
  return out;
}
